import { useCallback, useEffect, useMemo } from 'react';
import { LaunchBulkEditCommission } from 'src/components/common/BulkActions/BulkEditCommissionAction';
import { BulkActionItem } from 'src/components/Listings/InventoryActionDropdown/components/types';
import { useUpdatePurchasesPermissions } from 'src/components/Purchases/BulkActions/useUpdatePurchasesPermissions';
import {
  IPosEntity,
  useActivePosEntityContext,
} from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useCommandMenuContext } from 'src/contexts/CommandMenuContext/CommandMenuContext';
import { DialogId } from 'src/contexts/DialogContext/DialogContext';
import { useDialog } from 'src/contexts/DialogContext/useDialog';
import { BulkEditCommissionDialog } from 'src/dialogs/BulkEdits/BulkEditCommissionDialog';
import { useEventCallback } from 'src/hooks/useEventCallback';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  BulkEditPreviewWithDetails,
  BulkEditSaleClient,
  CommandOptionType,
  Commission,
  Event,
  SaleQuery,
} from 'src/WebApiController';

import { SALES_BULK_UPDATE_COMMISSION_UPDATE_KEY } from '../../SalesActionDropdown.constants';

export const useBulkEditCommissionAction = (
  filterQueryWithEventIds: SaleQuery,
  setIsLoading: (v: boolean) => void,
  isLoading?: boolean,
  event?: Event
) => {
  const { registerCommand, unregisterCommand } = useCommandMenuContext();
  const { hasPurchaseUpdateCommissionPermission } =
    useUpdatePurchasesPermissions();

  const { activeAccountWebClientConfig } = useAppContext();
  const { setActivePosEntity } = useActivePosEntityContext<IPosEntity>();
  const updateKey = event
    ? `saleCommission-event-${event.virtId}`
    : SALES_BULK_UPDATE_COMMISSION_UPDATE_KEY;
  const {
    eventsExpansion: { setListItemExpansion, refreshExpandedListItems },
  } = useCatalogDataContext();
  const { showErrorDialog } = useClientLogContext();

  const {
    openDialog: openBulkEditCommissionDialog,
    closeDialog: closeBulkEditCommissionDialog,
  } = useDialog(DialogId.BulkEditCommission, BulkEditCommissionDialog);

  const onSubmit = useCallback(
    async (
      commissions: Commission[] | null,
      supportBackgroundProcess?: boolean,
      onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
      preview?: BulkEditPreviewWithDetails
    ) => {
      if (activeAccountWebClientConfig.activeAccountId == null) {
        return null;
      }

      setIsLoading(true);

      if (onPreviewReceived) {
        await tryInvokeApi(
          async () => {
            const preview = await new BulkEditSaleClient(
              activeAccountWebClientConfig
            ).bulkUpdatePurchaseCommissionsPreview(filterQueryWithEventIds);

            onPreviewReceived(preview);
          },
          (error) => {
            showErrorDialog('bulkUpdatePurchaseCommissionsPreview', error, {
              trackErrorData: { filterQueryWithEventIds },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      } else {
        await tryInvokeApi(
          async () => {
            const succeeded = await new BulkEditSaleClient(
              activeAccountWebClientConfig
            ).bulkUpdatePurchaseCommissions(
              {
                item1: preview!.preview,
                item2: commissions ?? [],
              },
              updateKey,
              supportBackgroundProcess
            );

            if (!supportBackgroundProcess) {
              if (succeeded) {
                // Invalidate the activePosEntity, so that when clicking on details modal
                // we force re-loading the entity and newest pricing settings can be displayed
                setActivePosEntity(0);
                await refreshExpandedListItems();
              }

              closeBulkEditCommissionDialog();
            }
          },
          (error) => {
            showErrorDialog('bulkUpdatePurchaseCommissions', error, {
              trackErrorData: { commissions, preview },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      }
    },
    [
      activeAccountWebClientConfig,
      setIsLoading,
      filterQueryWithEventIds,
      showErrorDialog,
      updateKey,
      closeBulkEditCommissionDialog,
      setActivePosEntity,
      refreshExpandedListItems,
    ]
  );

  const onLaunchAction = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (event) {
        setListItemExpansion(true, [event.virtId]);
      }
      openBulkEditCommissionDialog({
        updateKey,
        onOkay: onSubmit,
        onCancel: closeBulkEditCommissionDialog,
        onClosed: () => {
          setIsLoading(false);
        },
      });
    },
    [
      closeBulkEditCommissionDialog,
      event,
      onSubmit,
      openBulkEditCommissionDialog,
      setIsLoading,
      setListItemExpansion,
      updateKey,
    ]
  );

  const openDialogStableRef = useEventCallback(() => {
    if (event) {
      setListItemExpansion(true, [event.virtId]);
    }
    openBulkEditCommissionDialog({
      updateKey,
      onOkay: onSubmit,
      onCancel: closeBulkEditCommissionDialog,
      onClosed: () => {
        setIsLoading(false);
      },
    });
  });

  useEffect(() => {
    registerCommand({
      command: {
        id: CommandOptionType.SalesBulkEditCommission,
        onSelect: openDialogStableRef,
      },
      disabled: !hasPurchaseUpdateCommissionPermission,
    });

    return () => {
      unregisterCommand({
        commandId: CommandOptionType.SalesBulkEditCommission,
        disabled: !hasPurchaseUpdateCommissionPermission,
      });
    };
  }, [
    hasPurchaseUpdateCommissionPermission,
    openDialogStableRef,
    registerCommand,
    unregisterCommand,
  ]);

  const actionItems = useMemo<BulkActionItem[]>(() => {
    return [
      {
        title: ContentId.UpdateBuyerCommission,
        onClick: onLaunchAction,
        disabled: isLoading,
      },
    ];
  }, [isLoading, onLaunchAction]);

  return {
    dropDown: (
      <LaunchBulkEditCommission
        key="EditCommission"
        onClick={onLaunchAction}
        disabled={isLoading}
      />
    ),
    actionItems,
  };
};
